import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import statisticService from '../../../services/statistics';
import sellerStatisticService from '../../../services/seller/statistics';
import deliverymanStatisticService from '../../../services/deliveryman/statistics';
import waiterStatisticService from '../../../services/waiter/statistics';
import moment from 'moment';

const initialState = {
  loading: false,
  counts: {},
  error: '',
};

// Helper function to build API params with proper priority
const buildApiParams = (params = {}) => {
  const apiParams = { ...params };
  
  // If date_from is provided, use date range and remove time
  if (apiParams.date_from) {
    apiParams.date_to = apiParams.date_to || moment().format('YYYY-MM-DD');
    delete apiParams.time;
  } else if (!apiParams.time) {
    // Default to subMonth if neither date_from nor time is provided
    apiParams.time = 'subMonth';
  }
  
  return apiParams;
};

export const fetchStatistics = createAsyncThunk(
  'statistics/fetchStatistics',
  (params = {}) => {
    const apiParams = buildApiParams(params);
    return statisticService.getAll(apiParams).then((res) => res);
  }
);

export const fetchSellerStatisticsCount = createAsyncThunk(
  'statistics/fetchSellerStatisticsCount',
  (params = {}) => {
    const apiParams = buildApiParams(params);
    return sellerStatisticService.getAll(apiParams).then((res) => res);
  }
);

export const fetchDeliverymanStatisticsCount = createAsyncThunk(
  'statistics/fetchDeliverymanStatisticsCount',
  (params = {}) => {
    return deliverymanStatisticService.getAllCount(params).then((res) => res);
  }
);

export const fetchWaiterStatisticsCount = createAsyncThunk(
  'statistics/fetchWaiterStatisticsCount',
  (params = {}) => {
    return waiterStatisticService.getAllCount(params).then((res) => res);
  }
);

const statisticsCountSlice = createSlice({
  name: 'statisticsCount',
  initialState,
  extraReducers: (builder) => {
    // admin
    builder.addCase(fetchStatistics.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchStatistics.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.counts = payload.data;
      state.error = '';
    });
    builder.addCase(fetchStatistics.rejected, (state, action) => {
      state.loading = false;
      state.counts = {};
      state.error = action.error.message;
    });

    // seller
    builder.addCase(fetchSellerStatisticsCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchSellerStatisticsCount.fulfilled, (state, action) => {
      const { payload } = action;

      state.loading = false;
      state.counts = payload.data;
      state.error = '';
    });
    builder.addCase(fetchSellerStatisticsCount.rejected, (state, action) => {
      state.loading = false;
      state.counts = {};
      state.error = action.error.message;
    });

    // delivery
    builder.addCase(fetchDeliverymanStatisticsCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      fetchDeliverymanStatisticsCount.fulfilled,
      (state, action) => {
        const { payload } = action;
        state.loading = false;
        state.counts = payload.data;
        state.error = '';
      }
    );
    builder.addCase(
      fetchDeliverymanStatisticsCount.rejected,
      (state, action) => {
        state.loading = false;
        state.counts = {};
        state.error = action.error.message;
      }
    );

    // waiter
    builder.addCase(fetchWaiterStatisticsCount.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchWaiterStatisticsCount.fulfilled, (state, action) => {
      const { payload } = action;
      state.loading = false;
      state.counts = payload.data;
      state.error = '';
    });
    builder.addCase(fetchWaiterStatisticsCount.rejected, (state, action) => {
      state.loading = false;
      state.counts = {};
      state.error = action.error.message;
    });
  },
});

export default statisticsCountSlice.reducer;
